import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { get } from 'lodash';
import { Typography } from '@mui/material';
import { createPractitioner, getPractitioner, updatePractitioner } from './_api';
import { searchOrganizations } from '../Organizations/_api';
import { IPractitionerForm } from './_types';
import PractitionerFormComponent from './PractitionerFormComponent';
import Button from 'components/Buttons/Button';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import useAlerts from 'components/Alerts/useAlerts';
import { Papeer } from 'components/Papeer/Papeer';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useWithEntityTitle } from 'utils/hooks/useWithEntityTitle';
import { getNameTranslated } from '../_grid';
import { format } from 'date-fns';

const linkBack = '/administration/patientRegistry?tab=practitioners';
const BIRTH_NUMBER_SYSTEM = 'http://hl7.cz/fhir/NamingSystem/cz-rcis';
const INSURANCE_SYSTEM = 'https://ncez.mzcr.cz/fhir/sid/cpoj';
const LICENSE_NUMBER_SYSTEM = 'http://hl7.cz/fhir/NamingSystem/cz-doctor-license';
const ICO_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v2-0203';
const IDENTIFIER_TYPE_INSURANCE = 'insurance';
const IDENTIFIER_TYPE_BIRTH_NUMBER = 'birthNumber';
const IDENTIFIER_TYPE_OTHER = 'other';
const IDENTIFIER_TYPE_LICENSE_NUMBER = 'licenseNumber';
const IDENTIFIER_TYPE_ICO = 'ico';

const PractitionerForm: React.FC = () => {
  const { t } = useTranslation('Practitioners');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toggleLoader } = useAppGlobals();
  const { addSuccessAlert, addErrorAlert } = useAlerts();
  const { compactMode } = useAppInfo();

  const isCreating = id === 'create';
  const [entity, setEntity] = useState<any>(null);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [orgItems, setOrgItems] = useState<{ id: number; name: string }[]>([]);

  const [defaultValues, setDefaultValues] = useState<IPractitionerForm>({
    name: [{ family: '', given: [''], prefix: [''], suffix: [''] }],
    active: true,
    identifier: [],
    telecom: [],
    address: [],
    organizationIds: [],
    gender: undefined,
    birthDate: undefined,
  });

  const { title } = useWithEntityTitle(
    isCreating,
    entity,
    t,
    'createTitle',
    'editTitle',
    getNameTranslated(get(entity, 'name', [])),
  );

  const loadEntity = async () => {
    toggleLoader();

    try {
      const orgs = await searchOrganizations({});
      const resolvedOrgs = orgs || [];
      setOrgItems(resolvedOrgs.map((o: any) => ({ id: o.id, name: o.name })));

      if (!isCreating && id) {
        const data = await getPractitioner(parseInt(id));
        if (data) {
          setEntity(data);
          const apiOrgIds: number[] = get(data, 'organizationIds', []);
          const defaultValues = {
            id: data.id,
            name: data.name || [{ family: '' }],
            active: get(data, 'active', true),
            identifier: get(data, 'identifier', []).map((identifier: any) => {
              const system = get(identifier, 'system', '');
              if (system === BIRTH_NUMBER_SYSTEM) {
                return {
                  ...identifier,
                  uiSystemType: IDENTIFIER_TYPE_BIRTH_NUMBER,
                  oid: '',
                };
              }
              if (system === INSURANCE_SYSTEM) {
                return {
                  ...identifier,
                  uiSystemType: IDENTIFIER_TYPE_INSURANCE,
                  oid: '',
                };
              }
              if (system === LICENSE_NUMBER_SYSTEM) {
                return {
                  ...identifier,
                  uiSystemType: IDENTIFIER_TYPE_LICENSE_NUMBER,
                  oid: '',
                };
              }
              if (system === ICO_SYSTEM) {
                return {
                  ...identifier,
                  uiSystemType: IDENTIFIER_TYPE_ICO,
                  oid: '',
                };
              }
              return {
                ...identifier,
                uiSystemType: IDENTIFIER_TYPE_OTHER,
                oid: system,
              };
            }),
            telecom: get(data, 'telecom', []),
            address: get(data, 'address', []),
            gender: get(data, 'gender', undefined),
            birthDate: get(data, 'birthDate')
              ? new Date(get(data, 'birthDate') as unknown as string)
              : undefined,
            organizationIds: resolvedOrgs
              .filter((o: any) => apiOrgIds.includes(o.id))
              .map((o: any) => ({ id: o.id, name: o.name })),
          } as unknown as IPractitionerForm;
          setDefaultValues(defaultValues);
        }
      }
    } catch (error) {
      addErrorAlert(t('loadError'));
      console.error('Error loading practitioner:', error);
    }

    setIsFetching(false);
    toggleLoader(false);
  };

  const handleSubmit = async (data: any) => {
    toggleLoader();
    try {
      const identifiers = (data.identifier || [])
        .filter((identifier: any) => get(identifier, 'value', '').trim() !== '')
        .map((identifier: any) => {
          const uiSystemType = get(identifier, 'uiSystemType', IDENTIFIER_TYPE_OTHER);
          let system = get(identifier, 'system', '');

          if (uiSystemType === IDENTIFIER_TYPE_BIRTH_NUMBER) {
            system = BIRTH_NUMBER_SYSTEM;
          } else if (uiSystemType === IDENTIFIER_TYPE_INSURANCE) {
            system = INSURANCE_SYSTEM;
          } else if (uiSystemType === IDENTIFIER_TYPE_LICENSE_NUMBER) {
            system = LICENSE_NUMBER_SYSTEM;
          } else if (uiSystemType === IDENTIFIER_TYPE_ICO) {
            system = ICO_SYSTEM;
          } else if (uiSystemType === IDENTIFIER_TYPE_OTHER) {
            system = get(identifier, 'oid', '').trim();
          }

          return {
            ...identifier,
            system,
            oid: undefined,
            uiSystemType: undefined,
          };
        })
        .filter((identifier: any) => get(identifier, 'system', '').trim() !== '');

      const birthDate = data.birthDate;
      const gender = data.gender;
      const names = (data.name || []).map((name: any) => ({
        ...name,
        given: (name.given || []).filter((value: string) => !!value),
        prefix: (name.prefix || []).filter((value: string) => !!value),
        suffix: (name.suffix || []).filter((value: string) => !!value),
      }));
      const payload = {
        ...data,
        name: names,
        identifier: identifiers,
        birthDate: birthDate ? format(birthDate, 'yyyy-MM-dd') : null,
        gender: gender ? gender : null,
        organizationIds: (data.organizationIds || []).map((o: any) => o.id),
      } as IPractitionerForm;

      if (isCreating) {
        await createPractitioner(payload);
        addSuccessAlert(t('createSuccess'));
      } else {
        await updatePractitioner({ ...payload, id: parseInt(id!) });
        addSuccessAlert(t('updateSuccess'));
      }
      navigate(linkBack);
    } catch (error: any) {
      const errorMessage = isCreating ? t('createError') : t('updateError');
      addErrorAlert(errorMessage);
      console.error('Error saving practitioner:', error);
    } finally {
      toggleLoader(false);
    }
  };

  useEffect(() => {
    loadEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isFetching) return null;

  if (!isCreating && !entity) {
    return (
      <>
        <Papeer>
          <Typography>{t('notFound')}</Typography>

          <Button
            variant="contained"
            size="large"
            {...({ to: linkBack } as any)}
            component={undefined}
          >
            {t('back')}
          </Button>
        </Papeer>
      </>
    );
  }

  return (
    <PractitionerFormComponent
      isCreating={isCreating}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      linkBack={linkBack}
      compactMode={compactMode}
      title={title}
      organizationItems={orgItems}
    />
  );
};

export default PractitionerForm;
